// Public events page at /events — magical calendar design.
// Reads from config/publicEvents + config/liveShift (both public-readable).
//
// Layout:
//   1. Spotlight section for promoted events (isPinned)
//   2. Interactive month calendar with warm glows, color-coded event types,
//      and cycling pancake photos on past dates
//   3. Next 10 upcoming events (series collapsed with recurrence hints)

import { useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import SiteNav from "./components/SiteNav";
import SiteFooter from "./sections/SiteFooter";
import PanBar from "./components/PanBar";

// ── Constants ────────────────────────────────────────────────────────────────
const BG = "#081a2a";
const GCAL_SUBSCRIBE_URL = "https://calendar.google.com/calendar/render?cid=c76d522983fab703fed4827bfedab4cbd78a8163817af387ace44f441c039403810@group.calendar.google.com";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Event type → glow color. Derived from eventType, keywords in event name,
// or falls back to warm gold.
function eventGlow(ev) {
  const name = ((ev.publicTitle || ev.eventName) + " " + (ev.eventType || "")).toLowerCase();
  if (name.includes("stream") || name.includes("twitch") || name.includes("live"))
    return { bg: "rgba(74,164,225,0.18)", glow: "rgba(74,164,225,0.45)", dot: "#4aa4e1" }; // sky — livestream
  if (name.includes("fest") || name.includes("rave") || name.includes("ticket") || name.includes("party"))
    return { bg: "rgba(239,83,126,0.18)", glow: "rgba(239,83,126,0.45)", dot: "#ef537e" }; // pink — ticketed/party
  if (name.includes("brunch") || name.includes("dinner") || name.includes("supper"))
    return { bg: "rgba(239,83,126,0.18)", glow: "rgba(239,83,126,0.4)", dot: "#ef537e" }; // pink — meal event (MYC pan-flag)
  return { bg: "rgba(252,179,31,0.15)", glow: "rgba(252,179,31,0.4)", dot: "#fcb31f" }; // gold — default
}

// ── Date helpers ─────────────────────────────────────────────────────────────
// confirmedDateMs is stored as UTC midnight by the Cloud Function. Calendar
// grid logic must use UTC accessors so dates don't shift back a day in
// Central time. Display-oriented helpers (time, long date) use confirmedStartMs
// which carries the real local-wall-clock time from Google Calendar.

// Convert an event's confirmedDateMs (UTC midnight) to a local Date whose
// Y/M/D matches the intended calendar day regardless of client timezone.
function eventDay(ms) {
  if (!ms) return null;
  const d = new Date(ms);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function toDateKey(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
function eventDateKey(ms) { const d = eventDay(ms); return d ? toDateKey(d) : null; }
function fmtTime(ms) { return ms ? new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null; }
function fmtDateShort(ms) {
  if (!ms) return null;
  // Use UTC-derived day to avoid off-by-one, but format for display
  const d = eventDay(ms);
  return d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : null;
}
function fmtDateLong(ms) {
  if (!ms) return null;
  const d = eventDay(ms);
  return d ? d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : null;
}
function fmtMonthYear(d) { return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; }
function daysUntil(ms) { return ms ? Math.ceil((ms - Date.now()) / 86400000) : Infinity; }
function mapsUrl(addr) { return addr ? `https://maps.google.com/?q=${encodeURIComponent(addr)}` : null; }

// Abbreviated time for cell display: "8a", "10a", "2p", "12p"
function fmtTimeAbbr(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  let h = d.getHours(), m = d.getMinutes();
  const ap = h >= 12 ? "p" : "a";
  if (h > 12) h -= 12; if (h === 0) h = 12;
  return m ? `${h}:${String(m).padStart(2,"0")}${ap}` : `${h}${ap}`;
}

// ── .ics generation ──────────────────────────────────────────────────────────
function toIcsDate(ms) {
  if (!ms) return null;
  const d = new Date(ms), p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth()+1)}${p(d.getDate())}T${p(d.getHours())}${p(d.getMinutes())}00`;
}
export function downloadIcs(ev) {
  const start = toIcsDate(ev.confirmedStartMs || ev.confirmedDateMs);
  const end = toIcsDate(ev.confirmedEndMs || (ev.confirmedDateMs ? ev.confirmedDateMs + 4*3600000 : null));
  if (!start) return;
  const lines = [
    "BEGIN:VCALENDAR","VERSION:2.0","PRODID:-//Joy of Pancakes//Events//EN","BEGIN:VEVENT",
    `DTSTART:${start}`, end ? `DTEND:${end}` : "",
    `SUMMARY:${(ev.publicTitle || ev.eventName || "Joy of Pancakes").replace(/[,;\\]/g, " ")}`,
    ev.venueAddress ? `LOCATION:${ev.venueAddress.replace(/[,;\\]/g, " ")}` : "",
    "END:VEVENT","END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
  const blob = new Blob([lines], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url;
  a.download = `${(ev.publicTitle || "event").replace(/\s+/g, "-").toLowerCase()}.ics`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ── Calendar add + share ─────────────────────────────────────────────────────
// Google Calendar "add event" template URL — one tap, syncs immediately, no
// file download. UTC (Z) dates so Google adjusts to the viewer's own zone.
export function gcalUrl(ev) {
  const startMs = ev.confirmedStartMs || ev.confirmedDateMs;
  if (!startMs) return null;
  const endMs = ev.confirmedEndMs || startMs + 4 * 3600000;
  const z = ms => new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.publicTitle || ev.eventName || "The Joy of Pancakes",
    dates: `${z(startMs)}/${z(endMs)}`,
  });
  if (ev.venueAddress || ev.publicLocation) p.set("location", ev.venueAddress || ev.publicLocation);
  if (ev.publicDescription) p.set("details", ev.publicDescription.slice(0, 500));
  return `https://calendar.google.com/calendar/render?${p}`;
}

export function eventShareUrl(ev) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://thejoyofpancakes.com";
  return `${origin}/events/${ev.bookingId}`;
}

// Native share sheet on mobile; clipboard fallback on desktop.
// Returns true if it fell back to copy (so the caller can show "Link copied!").
export async function shareEvent(ev) {
  const url = eventShareUrl(ev);
  const title = ev.publicTitle || ev.eventName || "The Joy of Pancakes";
  if (typeof navigator !== "undefined" && navigator.share) {
    try { await navigator.share({ title, text: `${title} — come find us!`, url }); return false; }
    catch { return false; } // user cancelled — no fallback needed
  }
  try { await navigator.clipboard.writeText(url); } catch {}
  return true;
}

// ── Main component ───────────────────────────────────────────────────────────
export default function EventsPage() {
  const [allEvents, setAllEvents] = useState(null);
  const [upcoming, setUpcoming]   = useState([]);
  const [liveShift, setLiveShift] = useState(null);
  const [calMonth, setCalMonth]   = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [selectedDay, setSelectedDay] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [focusedEvent, setFocusedEvent] = useState(null);

  // ── Debug toggle panel (/events?debug=1) ─────────────────────────────────
  // Lets you disable individual features to isolate memory usage. Check
  // Brave Task Manager after each toggle to identify the expensive one.
  const showDebug = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";
  const [dbg, setDbg] = useState({
    firestore: true,    // Subscribe to Firestore docs
    calendar: true,     // Render the month grid
    particles: true,    // Fairy dust on cells + header
    cellGlows: true,    // Bottom-edge gradient glows on cells
    pastPhotos: true,   // Static pancake photos in past cells
    upNext: true,       // Up Next list
    spotlight: true,    // Spotlight/pinned section
    headerDust: true,   // Header bar particles
  });
  function tog(key) { setDbg(d => ({ ...d, [key]: !d[key] })); }

  const calRef = useRef(null);

  function handleSelectDay(day, cellEl) {
    if (selectedDay && sameDay(selectedDay, day)) {
      setSelectedDay(null);
      setPopoverAnchor(null);
    } else {
      setSelectedDay(day);
      if (cellEl) {
        const r = cellEl.getBoundingClientRect();
        const calRect = calRef.current?.getBoundingClientRect();
        setPopoverAnchor({
          top: r.top, bottom: r.bottom,
          left: r.left, right: r.right,
          cellCenterX: r.left + r.width / 2,
          calLeft: calRect?.left ?? 0,
          calRight: calRect?.right ?? window.innerWidth,
        });
      }
    }
  }

  useEffect(() => { document.title = "Events · The Joy of Pancakes"; return () => { document.title = "The Joy of Pancakes"; }; }, []);

  useEffect(() => {
    if (!dbg.firestore) { setAllEvents([]); setUpcoming([]); setLiveShift(null); return; }
    const unsub1 = onSnapshot(doc(db, "config", "publicEvents"), s => {
      if (!s.exists()) { setAllEvents([]); setUpcoming([]); return; }
      const d = s.data();
      setAllEvents(d?.items || []);
      setUpcoming(d?.upcoming || []);
    }, () => { setAllEvents([]); setUpcoming([]); });
    const unsub2 = onSnapshot(doc(db, "config", "liveShift"), s => setLiveShift(s.exists() ? s.data() : null), () => setLiveShift(null));
    return () => { unsub1(); unsub2(); };
  }, [dbg.firestore]);

  const liveBookingId = liveShift?.isLive ? liveShift.bookingId : null;
  const today = new Date();

  // Index events by date key — ONLY for the currently viewed month so we
  // don't create DOM elements for all 142 events at once. Months the user
  // hasn't navigated to stay untouched in the raw allEvents array.
  const byDate = useMemo(() => {
    if (!allEvents) return {};
    const map = {};
    const mStart = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
    const mEnd   = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0);
    for (const ev of allEvents) {
      const d = eventDay(ev.confirmedDateMs);
      if (!d) continue;
      if (d < mStart || d > mEnd) continue; // skip events outside viewed month
      const key = toDateKey(d);
      (map[key] = map[key] || []).push(ev);
    }
    return map;
  }, [allEvents, calMonth]);

  // Pinned events (use raw ms for future-check — that's fine, we're comparing epoch not calendar day)
  const pinned = useMemo(() => (allEvents || []).filter(ev => ev.isPinned && (ev.confirmedDateMs ?? 0) >= Date.now() - 86400000), [allEvents]);

  // Day modal events
  const dayEvents = selectedDay ? (byDate[toDateKey(selectedDay)] || []) : [];
  const dayIsFuture = selectedDay && selectedDay >= today && !dayEvents.length;

  return (
    <div className="jop-events-page" style={{ background: BG, minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)", position: "relative", overflow: "hidden" }}>
      <style>{`
        .jop-cal-cell { aspect-ratio: 1/1; }
        @keyframes jopCellPulse { 0%,100%{opacity:0.4} 50%{opacity:1} }
        @keyframes jopPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes jopPopIn { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
        @keyframes jopPopCentered { from{opacity:0;transform:translateX(-50%) scale(0.92)} to{opacity:1;transform:translateX(-50%) scale(1)} }
        @keyframes jopPopFade { from{opacity:0} to{opacity:1} }

        @keyframes jopSpotSway {
          0%   { transform: rotate(var(--spot-base)) scaleX(1); }
          25%  { transform: rotate(calc(var(--spot-base) + 2deg)) scaleX(1.04); }
          50%  { transform: rotate(calc(var(--spot-base) - 1deg)) scaleX(0.97); }
          75%  { transform: rotate(calc(var(--spot-base) + 1.5deg)) scaleX(1.02); }
          100% { transform: rotate(var(--spot-base)) scaleX(1); }
        }
        @keyframes jopSpotFlicker {
          0%,100% { opacity: var(--spot-opacity); }
          30%  { opacity: calc(var(--spot-opacity) * 0.85); }
          60%  { opacity: calc(var(--spot-opacity) * 1.1); }
          85%  { opacity: calc(var(--spot-opacity) * 0.92); }
        }
      `}</style>

      {/* Theatre spotlight beams — emanate from top corners */}
      <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        {/* Left beam — warm gold */}
        <div style={{
          position: "absolute", top: "-5%", left: "-2%",
          width: "55%", height: "110%",
          background: "conic-gradient(from 160deg at 0% 0%, transparent 0deg, rgba(252,179,31,0.08) 8deg, rgba(252,179,31,0.18) 18deg, rgba(252,179,31,0.08) 30deg, transparent 42deg)",
          transformOrigin: "0% 0%",
          "--spot-base": "0deg", "--spot-opacity": "1",
          animation: "jopSpotSway 20s ease-in-out infinite, jopSpotFlicker 8s ease-in-out infinite",
        }} />
        {/* Left beam — inner hot core */}
        <div style={{
          position: "absolute", top: "-5%", left: "-2%",
          width: "40%", height: "100%",
          background: "conic-gradient(from 164deg at 0% 0%, transparent 0deg, rgba(255,218,173,0.04) 12deg, rgba(252,179,31,0.1) 18deg, rgba(255,218,173,0.04) 24deg, transparent 34deg)",
          transformOrigin: "0% 0%",
          "--spot-base": "2deg", "--spot-opacity": "0.8",
          animation: "jopSpotSway 25s ease-in-out infinite reverse, jopSpotFlicker 12s ease-in-out infinite",
          filter: "blur(8px)",
        }} />

        {/* Right beam — soft pink */}
        <div style={{
          position: "absolute", top: "-5%", right: "-2%",
          width: "55%", height: "110%",
          background: "conic-gradient(from 190deg at 100% 0%, transparent 0deg, rgba(239,83,126,0.07) 8deg, rgba(239,83,126,0.15) 18deg, rgba(239,83,126,0.07) 30deg, transparent 42deg)",
          transformOrigin: "100% 0%",
          "--spot-base": "0deg", "--spot-opacity": "1",
          animation: "jopSpotSway 22s ease-in-out infinite reverse, jopSpotFlicker 9s ease-in-out infinite",
        }} />
        {/* Right beam — inner hot core */}
        <div style={{
          position: "absolute", top: "-5%", right: "-2%",
          width: "40%", height: "100%",
          background: "conic-gradient(from 194deg at 100% 0%, transparent 0deg, rgba(255,218,173,0.03) 12deg, rgba(239,83,126,0.08) 18deg, rgba(255,218,173,0.03) 24deg, transparent 34deg)",
          transformOrigin: "100% 0%",
          "--spot-base": "-2deg", "--spot-opacity": "0.7",
          animation: "jopSpotSway 28s ease-in-out infinite, jopSpotFlicker 14s ease-in-out infinite",
          filter: "blur(10px)",
        }} />

        {/* Center stage glow — warm pool of light where the content lives */}
        <div style={{
          position: "absolute", top: "8%", left: "50%", transform: "translateX(-50%)",
          width: "70%", height: "40%",
          background: "radial-gradient(ellipse 100% 80% at 50% 0%, rgba(252,179,31,0.06) 0%, transparent 70%)",
          filter: "blur(20px)",
        }} />
      </div>

      <SiteNav />
      <main style={{ maxWidth: "860px", margin: "0 auto", padding: "5.5rem 1.25rem 4rem", position: "relative", zIndex: 1 }}>

        {/* Header */}
        <header style={{ textAlign: "center", marginBottom: "2rem" }}>
          <PanBar width={88} height={4} margin="0 auto 1.2rem" />
          <h1 style={{ margin: "0 0 0.5rem", fontFamily: "var(--jop-script)", fontSize: "clamp(2.4rem, 6vw, 3.6rem)", color: "var(--jop-gold)", fontWeight: 700, lineHeight: 1.1 }}>
            Where to find us.
          </h1>
          <p style={{ margin: "0 auto", maxWidth: "560px", color: "rgba(255,218,173,0.7)", fontSize: "1rem", lineHeight: 1.55 }}>
            Farmers markets, pop-ups, and the occasional curated appearance.
            Want us at <a href="/quote" style={{ color: "var(--jop-pink)", fontWeight: 700, textDecoration: "none" }}>your event</a>?
          </p>
        </header>

        {allEvents === null && (
          <div style={{ textAlign: "center", color: "rgba(255,218,173,0.5)", padding: "3rem" }}>Loading events…</div>
        )}

        {/* ── Debug toggle panel (/events?debug=1) ──────────────── */}
        {showDebug && (
          <div style={{
            background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,218,173,0.3)",
            borderRadius: "12px", padding: "0.75rem 1rem", marginBottom: "1.5rem",
            fontSize: "0.78rem", color: "rgba(255,218,173,0.8)",
          }}>
            <div style={{ fontWeight: 800, marginBottom: "0.5rem", color: "var(--jop-gold)" }}>
              Memory Debug — toggle features and check Brave Task Manager
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
              {Object.entries(dbg).map(([key, val]) => (
                <button key={key} onClick={() => tog(key)} style={{
                  background: val ? "rgba(112,190,68,0.2)" : "rgba(239,83,126,0.15)",
                  border: `1px solid ${val ? "rgba(112,190,68,0.5)" : "rgba(239,83,126,0.4)"}`,
                  color: val ? "#70be44" : "#ef537e",
                  borderRadius: "999px", padding: "0.3rem 0.7rem",
                  fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                  {val ? "ON" : "OFF"} {key}
                </button>
              ))}
            </div>
          </div>
        )}

        {allEvents && (
          <>
            {/* ── Spotlight ───────────────────────────────────────────── */}
            {dbg.spotlight && pinned.length > 0 && (
              <section style={{ marginBottom: "2rem" }}>
                <SectionLabel>Spotlight</SectionLabel>
                {pinned.map(ev => <SpotlightCard key={ev.bookingId} ev={ev} isLive={liveBookingId === ev.bookingId} />)}
              </section>
            )}

            {/* ── Magical calendar ────────────────────────────────────── */}
            {dbg.calendar ? <section ref={calRef} style={{ marginBottom: "2.5rem" }}>
              <MagicalCalendar
                month={calMonth}
                onPrev={() => setCalMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                onNext={() => setCalMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                onToday={() => setCalMonth(new Date(today.getFullYear(), today.getMonth(), 1))}
                byDate={byDate}
                today={today}
                selectedDay={selectedDay}
                onSelectDay={handleSelectDay}
                liveBookingId={liveBookingId}
                showHeaderDust={dbg.headerDust}
                showParticles={dbg.particles}
                showGlows={dbg.cellGlows}
                showPhotos={dbg.pastPhotos}
              />
            </section> : <div style={{ color: "rgba(255,218,173,0.3)", padding: "2rem", textAlign: "center" }}>Calendar disabled</div>}

            {/* ── Day modal ───────────────────────────────────────────── */}
            {selectedDay && (
              <DayModal
                day={selectedDay}
                events={dayEvents}
                isFuture={dayIsFuture}
                liveBookingId={liveBookingId}
                anchor={popoverAnchor}
                onClose={() => { setSelectedDay(null); setPopoverAnchor(null); }}
              />
            )}

            {/* ── Focused event modal (from Up Next tap) ──────────────── */}
            {focusedEvent && (
              <EventModal
                ev={focusedEvent}
                isLive={liveBookingId === focusedEvent.bookingId}
                onClose={() => setFocusedEvent(null)}
              />
            )}

            {/* ── Next 10 upcoming (series collapsed) ─────────────────── */}
            {dbg.upNext && upcoming.length > 0 && (
              <section style={{ marginBottom: "2rem" }}>
                <SectionLabel>Up Next</SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {upcoming.map(ev => (
                    <UpcomingRow key={ev.bookingId} ev={ev} isLive={liveBookingId === ev.bookingId}
                      onTap={() => setFocusedEvent(ev)} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Footer CTAs ─────────────────────────────────────────── */}
            <div style={{ textAlign: "center", marginTop: "2rem" }}>
              <a href={GCAL_SUBSCRIBE_URL} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", color: "var(--jop-gold)", border: "1px solid rgba(252,179,31,0.45)", borderRadius: "999px", padding: "0.55rem 1.25rem", fontSize: "0.85rem", fontWeight: 700, textDecoration: "none", marginBottom: "1.25rem" }}>
                Subscribe to our calendar →
              </a>
              <p style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.88rem", marginBottom: "1rem" }}>
                Don't see what you're looking for?
              </p>
              <a href="/quote" style={{
                display: "inline-flex", padding: "0.85rem 1.8rem",
                background: "linear-gradient(180deg, #ff6b95 0%, #ef537e 55%, #c93a66 100%)",
                color: "white", fontSize: "1rem", fontWeight: 800,
                textDecoration: "none", borderRadius: "999px",
                boxShadow: "0 8px 24px rgba(239,83,126,0.45)",
              }}>
                Book us for your event →
              </a>
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

// ── Section label ────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <h2 style={{
      margin: "0 0 0.85rem", fontFamily: "var(--jop-script)",
      fontSize: "clamp(1.5rem, 3.5vw, 2rem)", color: "var(--jop-gold)",
      fontWeight: 700, lineHeight: 1.1,
      paddingBottom: "0.45rem", borderBottom: "1px solid rgba(255,218,173,0.15)",
    }}>{children}</h2>
  );
}

// ── Spotlight card ────────────────────────────────────────────────────────────
function SpotlightCard({ ev, isLive }) {
  return (
    <a href={`/events/${ev.bookingId}`} style={{
      display: "block", textDecoration: "none", color: "inherit",
      background: "linear-gradient(135deg, rgba(252,179,31,0.1) 0%, rgba(239,83,126,0.08) 100%)",
      border: "2px solid rgba(252,179,31,0.4)", borderRadius: "18px",
      padding: "1.4rem 1.5rem", position: "relative", overflow: "hidden",
      boxShadow: "0 12px 40px rgba(252,179,31,0.12), 0 0 30px rgba(252,179,31,0.06)",
    }}>
      {isLive && <LiveBadge />}
      {ev.publicStatusMessage && <StatusBanner msg={ev.publicStatusMessage} />}
      <div style={{ fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--jop-pink)", marginBottom: "0.5rem" }}>
        Featured Event
      </div>
      <h3 style={{ margin: "0 0 0.35rem", fontFamily: "var(--jop-script)", fontSize: "clamp(1.6rem, 4.5vw, 2.3rem)", color: "white", fontWeight: 700, lineHeight: 1.15 }}>
        {ev.publicTitle || ev.eventName}
      </h3>
      <div style={{ color: "var(--jop-gold)", fontSize: "0.95rem", fontWeight: 700 }}>
        {fmtDateLong(ev.confirmedDateMs)}
        {ev.confirmedStartMs && `, ${fmtTime(ev.confirmedStartMs)}`}
        {ev.confirmedEndMs && ` – ${fmtTime(ev.confirmedEndMs)}`}
      </div>
      {(ev.publicLocation || ev.venueName) && (
        <div style={{ color: "rgba(255,218,173,0.6)", fontSize: "0.88rem", marginTop: "0.2rem" }}>{ev.publicLocation || ev.venueName}</div>
      )}
      {ev.publicDescription && (
        <p style={{ margin: "0.7rem 0 0", color: "rgba(255,218,173,0.72)", fontSize: "0.9rem", lineHeight: 1.6, maxHeight: "4.8em", overflow: "hidden" }}>
          {ev.publicDescription}
        </p>
      )}
      <div style={{ marginTop: "0.9rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
        {ev.ticketingUrl && <Chip bg="rgba(239,83,126,0.15)" border="rgba(239,83,126,0.45)" color="var(--jop-pink)">🎟 Get tickets</Chip>}
        <Chip bg="rgba(255,218,173,0.06)" border="rgba(255,218,173,0.2)" color="rgba(255,218,173,0.6)" style={{ marginLeft: "auto" }}>Details →</Chip>
      </div>
    </a>
  );
}

// ── Magical calendar ─────────────────────────────────────────────────────────
// Warm glowing cells for event days, color-coded by type. Past dates with
// pancake photos cycle a small cutout in the cell.
function MagicalCalendar({ month, onPrev, onNext, onToday, byDate, today, selectedDay, onSelectDay, liveBookingId, showHeaderDust = true, showParticles = true, showGlows = true, showPhotos = true }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  while (cells.length % 7) cells.push(null);

  const isCurrentMonth = month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth();

  return (
    <div style={{
      background: "rgba(14,46,70,0.6)",
      border: "1px solid rgba(255,218,173,0.12)",
      borderRadius: "20px", overflow: "hidden",
      boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
    }}>
      {/* Month nav — warm header with MYC pan-flag gradient glow along
          the bottom edge and ascending fairy-dust particles. */}
      <div style={{
        display: "flex", alignItems: "center", padding: "1.1rem 1.1rem 1.3rem",
        background: "radial-gradient(ellipse 120% 200% at 50% -30%, rgba(252,179,31,0.12) 0%, transparent 60%)",
        position: "relative", overflow: "hidden",
      }}>
        {/* Pan-flag MYC gradient hairline along bottom edge */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: "2px",
          background: "linear-gradient(90deg, var(--jop-pink) 0%, var(--jop-gold) 50%, var(--jop-highlight-blue) 100%)",
        }} />
        {/* Soft glow above the hairline */}
        <div style={{
          position: "absolute", bottom: 0, left: "5%", right: "5%", height: "18px",
          background: "linear-gradient(90deg, rgba(239,83,126,0.2) 0%, rgba(252,179,31,0.25) 50%, rgba(74,164,225,0.2) 100%)",
          filter: "blur(8px)",
          pointerEvents: "none",
        }} />
        {/* Fairy dust rising from the gradient edge */}
        {showHeaderDust && <CalendarHeaderDust />}

        <button onClick={onPrev} style={{ ...navBtn, position: "relative", zIndex: 1 }}>‹</button>
        <div style={{ flex: 1, textAlign: "center", position: "relative", zIndex: 1 }}>
          <span style={{ fontFamily: "var(--jop-script)", color: "var(--jop-gold)", fontWeight: 700, fontSize: "1.5rem", textShadow: "0 2px 12px rgba(252,179,31,0.25)" }}>
            {fmtMonthYear(month)}
          </span>
        </div>
        {!isCurrentMonth && (
          <button onClick={onToday} style={{ ...navBtn, marginRight: "0.55rem", fontSize: "0.72rem", padding: "0.3rem 0.65rem", position: "relative", zIndex: 1 }}>Today</button>
        )}
        <button onClick={onNext} style={{ ...navBtn, position: "relative", zIndex: 1 }}>›</button>
      </div>

      {/* Day headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid rgba(255,218,173,0.06)" }}>
        {DAYS.map(d => (
          <div key={d} style={{
            textAlign: "center", padding: "0.55rem 0",
            color: "rgba(255,218,173,0.35)", fontSize: "0.7rem", fontWeight: 700,
            letterSpacing: "0.1em", textTransform: "uppercase",
          }}>{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} style={{ aspectRatio: "1/1", background: "rgba(0,0,0,0.15)" }} />;
          return <CalendarCell key={toDateKey(day)} day={day} byDate={byDate} today={today}
            selectedDay={selectedDay} onSelect={onSelectDay} liveBookingId={liveBookingId}
            showParticlesFn={showParticles} showGlows={showGlows} showPhotos={showPhotos} />;
        })}
      </div>
    </div>
  );
}

// Individual calendar cell — lightweight. Particles ONLY on this-week cells
// to keep total animated elements under ~25 (vs ~200 before).
function CalendarCell({ day, byDate, today, selectedDay, onSelect, liveBookingId, showParticlesFn = true, showGlows = true, showPhotos = true }) {
  const key = toDateKey(day);
  const evts = byDate[key] || [];
  const isToday = sameDay(day, today);
  const isSelected = selectedDay && sameDay(day, selectedDay);
  const isPast = day < today && !isToday;
  const hasEvents = evts.length > 0;
  const hasLive = evts.some(e => e.bookingId === liveBookingId);
  const glow = hasEvents ? eventGlow(evts[0]) : null;

  // Particles on ALL future event cells (safe now — only current month renders)
  const showParticles = showParticlesFn && hasEvents && !isPast;

  // Past cells: one static photo (no cycling interval)
  const pastPhotos = showPhotos && isPast ? evts.flatMap(e => e.pastStats?.samplePhotoUrls || []).slice(0, 4) : [];

  // Bottom glow colors blended from all events on this day
  const glowColors = hasEvents && !isPast
    ? [...new Set(evts.map(e => eventGlow(e).glow))]
    : null;

  return (
    <button
      onClick={(e) => onSelect(day, e.currentTarget)}
      className="jop-cal-cell"
      style={{
        background: hasEvents && !isPast
          ? `radial-gradient(ellipse 90% 80% at 50% 100%, ${glow.bg} 0%, transparent 70%)`
          : isSelected ? "rgba(252,179,31,0.1)"
          : isToday ? "rgba(74,164,225,0.06)"
          : "transparent",
        border: "none",
        borderRight: "1px solid rgba(255,218,173,0.03)",
        borderBottom: "1px solid rgba(255,218,173,0.03)",
        cursor: "pointer",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "2px", padding: "2px",
        fontFamily: "inherit",
        position: "relative", overflow: "hidden",
        opacity: isPast && !hasEvents ? 0.3 : isPast ? 0.65 : 1,
      }}
    >
      {/* Bottom-edge gradient bar + soft glow */}
      {showGlows && glowColors && (
        <>
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, height: "2px",
            background: glowColors.length === 1 ? glowColors[0] : `linear-gradient(90deg, ${glowColors.join(", ")})`,
            opacity: 0.6, pointerEvents: "none",
          }} />
          <div style={{
            position: "absolute", bottom: 0, left: "5%", right: "5%", height: "40%",
            background: glowColors.length === 1
              ? `radial-gradient(ellipse 100% 100% at 50% 100%, ${glowColors[0]} 0%, transparent 70%)`
              : `linear-gradient(90deg, ${glowColors.join(", ")})`,
            opacity: 0.2, pointerEvents: "none",
          }} />
        </>
      )}

      {/* Fairy dust — only on this-week cells */}
      {showParticles && <CellDust color={glow.dot} count={isToday ? 5 : 3} seed={day.getDate()} />}

      {/* Live pulse ring */}
      {hasLive && (
        <div style={{
          position: "absolute", inset: "3px", borderRadius: "8px",
          border: "2px solid rgba(112,190,68,0.5)",
          animation: "jopCellPulse 2.5s ease-in-out infinite",
          pointerEvents: "none",
        }} />
      )}

      {/* Day number */}
      <span style={{
        color: isToday ? "var(--jop-highlight-blue)"
          : isSelected ? "var(--jop-gold)"
          : hasEvents ? "white"
          : "rgba(255,218,173,0.55)",
        fontSize: "0.85rem",
        fontWeight: isToday || isSelected || hasEvents ? 700 : 400,
        position: "relative", zIndex: 1,
      }}>
        {day.getDate()}
      </span>

      {/* Color-coded dots — no box-shadow (GPU-expensive) */}
      {hasEvents && !isPast && (
        <div style={{ display: "flex", gap: "3px", position: "relative", zIndex: 1, flexWrap: "wrap", justifyContent: "center" }}>
          {evts.slice(0, 4).map((e, j) => {
            const c = eventGlow(e);
            return <span key={j} style={{
              width: "6px", height: "6px", borderRadius: "50%",
              background: hasLive && e.bookingId === liveBookingId ? "#70be44" : c.dot,
              boxShadow: `0 0 5px ${hasLive && e.bookingId === liveBookingId ? "rgba(112,190,68,0.7)" : c.glow}`,
            }} />;
          })}
          {evts.length > 4 && (
            <span style={{ color: "rgba(255,218,173,0.4)", fontSize: "0.45rem", lineHeight: 1 }}>+{evts.length - 4}</span>
          )}
        </div>
      )}

      {/* Past date — cycling pancake cutout photos */}
      {pastPhotos.length > 0 && <CyclingPhoto photos={pastPhotos} />}
    </button>
  );
}

// Cycles through pancake cutout photos in a small cell. Changes every 3s.
function CyclingPhoto({ photos }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (photos.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % photos.length), 3000);
    return () => clearInterval(t);
  }, [photos.length]);
  return (
    <div style={{ width: "22px", height: "22px", position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <img src={photos[idx]} alt="" loading="lazy" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.6))" }} />
    </div>
  );
}

// ── Day popover (anchored to cell) ───────────────────────────────────────────
// Positioned near the clicked calendar cell. On narrow viewports it centers
// horizontally; vertically it prefers below the cell but flips above if the
// cell is in the lower half of the screen. Includes subtle sparkle particles.
function DayModal({ day, events, isFuture, liveBookingId, anchor, onClose }) {
  const popRef = useRef(null);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Compute position from anchor. Always anchor by `top` so the popover
  // grows downward when the user taps Details to expand content. If the
  // cell is in the lower half of the screen, position above the cell but
  // still use `top` (calculated from cell top minus estimated height) so
  // expansion still goes down.
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const vw = typeof window !== "undefined" ? window.innerWidth : 400;
  // Popover positioning: align based on which zone of the calendar the
  // cell is in. Left 2 columns → left-align to calendar edge. Right 2
  // columns → right-align to calendar edge. Middle 3 → center on cell.
  const pad = 12;
  const popW = Math.min(340, vw - pad * 2);
  const nearBottom = anchor && anchor.bottom > vh * 0.55;
  const anchorTop = anchor
    ? nearBottom
      ? Math.max(pad, anchor.top - 240)
      : anchor.bottom + 10
    : vh * 0.15;
  const maxH = vh - anchorTop - pad;

  const isMobile = vw < 600;
  let popLeft;

  if (isMobile) {
    // Mobile: dead center of viewport
    popLeft = null; // use left:50% + transform instead
  } else if (anchor) {
    // Desktop: zone-aligned to calendar edges
    const calL = anchor.calLeft ?? 0;
    const calR = anchor.calRight ?? vw;
    const calW = calR - calL;
    const cellRelX = (anchor.cellCenterX - calL) / calW;

    if (cellRelX < 0.3) {
      popLeft = Math.max(pad, calL);
    } else if (cellRelX > 0.7) {
      popLeft = Math.max(pad, Math.min(vw - popW - pad, calR - popW));
    } else {
      popLeft = Math.max(pad, Math.min(vw - popW - pad, anchor.cellCenterX - popW / 2));
    }
  } else {
    popLeft = null;
  }

  const popStyle = isMobile || popLeft == null ? {
    position: "fixed",
    top: `${anchorTop}px`,
    left: "50%",
    transform: "translateX(-50%)",
    width: `${popW}px`,
    maxWidth: `calc(100vw - ${pad * 2}px)`,
  } : {
    position: "fixed",
    top: `${anchorTop}px`,
    left: `${popLeft}px`,
    width: `${popW}px`,
  };

  // Determine dominant glow for sparkles
  const glowColor = events.length > 0 ? eventGlow(events[0]).dot : "var(--jop-gold)";

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(6,12,25,0.5)",
        zIndex: 200,
      }} />
      <div ref={popRef} style={{
        ...popStyle,
        maxHeight: `${Math.max(200, maxH)}px`,
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        background: "linear-gradient(180deg, #0e2644 0%, #0b1d34 100%)",
        border: "1px solid rgba(252,179,31,0.3)",
        borderRadius: "16px", padding: "1.1rem 1.1rem 1.25rem",
        zIndex: 201, fontFamily: "var(--jop-display)", color: "var(--jop-cream)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.55), 0 0 20px rgba(252,179,31,0.08)",
        animation: isMobile ? "jopPopFade 0.15s ease-out" : "jopPopIn 0.18s ease-out",
      }}>
        {/* Sparkles inside the popover */}
        <PopoverDust color={glowColor} />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", position: "relative", zIndex: 1 }}>
          <h3 style={{ margin: 0, fontFamily: "var(--jop-script)", fontSize: "1.35rem", color: "var(--jop-gold)" }}>
            {fmtDateLong(day.getTime())}
          </h3>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          {events.length === 0 && isFuture && (
            <div style={{ textAlign: "center", padding: "1.25rem 0" }}>
              <div style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.9rem", marginBottom: "0.85rem" }}>
                Nothing scheduled yet. Want us there?
              </div>
              <a href={`/quote?date=${toDateKey(day)}`} style={{
                display: "inline-flex", background: "rgba(239,83,126,0.12)", border: "1px solid rgba(239,83,126,0.4)",
                color: "var(--jop-pink)", borderRadius: "999px", padding: "0.5rem 1rem",
                fontSize: "0.82rem", fontWeight: 700, textDecoration: "none",
              }}>Inquire about this date →</a>
            </div>
          )}

          {events.length === 0 && !isFuture && (
            <div style={{ color: "rgba(255,218,173,0.45)", fontSize: "0.85rem", textAlign: "center", padding: "0.75rem 0" }}>
              No events on this day.
            </div>
          )}

          {events.map(ev => <DayModalCard key={ev.bookingId} ev={ev} isLive={liveBookingId === ev.bookingId} />)}
        </div>
      </div>
    </>
  );
}

// Truncate description to a blurb (~120 chars at a word boundary). If there's
// an explicit publicBlurb field we use that; otherwise we derive it.
const BLURB_LEN = 120;
function blurbOf(ev) {
  if (ev.publicBlurb) return ev.publicBlurb;
  const full = (ev.publicDescription || "").trim();
  if (full.length <= BLURB_LEN) return full;
  return full.slice(0, BLURB_LEN).replace(/\s+\S*$/, "") + "…";
}

function DayModalCard({ ev, isLive }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const addr = ev.venueAddress || ev.publicLocation;
  const maps = mapsUrl(addr);
  const gcal = gcalUrl(ev);
  const photos = ev.pastStats?.samplePhotoUrls || [];
  const isPast = ev.confirmedDateMs && ev.confirmedDateMs < Date.now() - 86400000;
  const fullDesc = (ev.publicDescription || "").trim();
  const blurb = blurbOf(ev);
  const hasMore = fullDesc.length > BLURB_LEN || (ev.publicLinks && ev.publicLinks.length > 0) || gcal;

  // Primary link — the one link that gets a visible button alongside Directions
  const primaryLink = (ev.publicLinks || []).find(l => l.isPrimary) || null;
  // Secondary links — shown after expanding Details
  const secondaryLinks = (ev.publicLinks || []).filter(l => !l.isPrimary);

  return (
    <div style={{
      background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,218,173,0.12)",
      borderRadius: "12px", padding: "1rem", marginBottom: "0.75rem",
    }}>
      {isLive && <LiveBadge />}
      {ev.publicStatusMessage && <StatusBanner msg={ev.publicStatusMessage} />}
      <h4 style={{ margin: "0 0 0.25rem", color: "white", fontWeight: 700, fontSize: "1.05rem" }}>
        {ev.publicTitle || ev.eventName}
      </h4>
      <div style={{ color: "var(--jop-gold)", fontSize: "0.85rem", fontWeight: 700 }}>
        {fmtTime(ev.confirmedStartMs)}{ev.confirmedEndMs && ` – ${fmtTime(ev.confirmedEndMs)}`}
      </div>
      {(ev.publicLocation || ev.venueName) && (
        <div style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.82rem", marginTop: "0.15rem" }}>{ev.publicLocation || ev.venueName}</div>
      )}

      {/* Blurb (always shown) → expands to full description on Details tap */}
      {blurb && !expanded && (
        <p style={{ margin: "0.5rem 0 0", color: "rgba(255,218,173,0.7)", fontSize: "0.85rem", lineHeight: 1.55 }}>
          {blurb}
        </p>
      )}
      {expanded && fullDesc && (
        <p style={{ margin: "0.5rem 0 0", color: "rgba(255,218,173,0.78)", fontSize: "0.88rem", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
          {fullDesc}
        </p>
      )}

      {/* Expanded: secondary links */}
      {expanded && secondaryLinks.length > 0 && (
        <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", marginTop: "0.6rem" }}>
          {secondaryLinks.map((link, i) => (
            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
              style={{ ...chipStyle("rgba(255,218,173,0.06)", "rgba(255,218,173,0.18)", "rgba(255,218,173,0.65)"), textDecoration: "none", fontSize: "0.74rem" }}>
              {link.label || link.url}
            </a>
          ))}
        </div>
      )}

      {/* Expanded: other-calendar (.ics) fallback for Apple Calendar / Outlook */}
      {expanded && gcal && (
        <div style={{ marginTop: "0.5rem" }}>
          <button onClick={e => { e.stopPropagation(); downloadIcs(ev); }}
            style={{ ...chipStyle("rgba(255,218,173,0.06)", "rgba(255,218,173,0.18)", "rgba(255,218,173,0.65)"), fontSize: "0.74rem" }}>
            📥 Other calendars (.ics)
          </button>
        </div>
      )}

      {/* Past event photo strip */}
      {isPast && photos.length > 0 && (
        <div style={{ display: "flex", gap: "0.3rem", marginTop: "0.6rem" }}>
          {photos.map((url, i) => (
            <div key={i} style={{ width: "40px", height: "40px", borderRadius: "6px", overflow: "hidden", background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={url} alt="" loading="lazy" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))" }} />
            </div>
          ))}
        </div>
      )}

      {/* Action row */}
      <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.7rem" }}>
        <button onClick={async e => { e.stopPropagation(); const fellBack = await shareEvent(ev); if (fellBack) { setCopied(true); setTimeout(() => setCopied(false), 2000); } }} style={chipStyle("rgba(239,83,126,0.12)", "rgba(239,83,126,0.4)", "var(--jop-pink)")}>
          {copied ? "Link copied!" : "🔗 Share"}
        </button>
        {gcal && <a href={gcal} target="_blank" rel="noopener noreferrer" style={{ ...chipStyle("rgba(252,179,31,0.12)", "rgba(252,179,31,0.4)", "var(--jop-gold)"), textDecoration: "none" }}>📅 Add to calendar</a>}
        {maps && <a href={maps} target="_blank" rel="noopener noreferrer" style={{ ...chipStyle("rgba(74,164,225,0.12)", "rgba(74,164,225,0.4)", "var(--jop-highlight-blue)"), textDecoration: "none" }}>📍 Directions</a>}
        {ev.ticketingUrl && <a href={ev.ticketingUrl} target="_blank" rel="noopener noreferrer" style={{ ...chipStyle("rgba(239,83,126,0.12)", "rgba(239,83,126,0.4)", "var(--jop-pink)"), textDecoration: "none" }}>🎟 Tickets</a>}
        {primaryLink && <a href={primaryLink.url} target="_blank" rel="noopener noreferrer" style={{ ...chipStyle("rgba(239,83,126,0.12)", "rgba(239,83,126,0.4)", "var(--jop-pink)"), textDecoration: "none" }}>{primaryLink.label || "Link"}</a>}
        {hasMore && (
          <button onClick={() => setExpanded(!expanded)} style={{ ...chipStyle("rgba(255,218,173,0.06)", "rgba(255,218,173,0.15)", "rgba(255,218,173,0.55)"), marginLeft: "auto" }}>
            {expanded ? "Less ▴" : "Details ▾"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Upcoming row (series-aware) ──────────────────────────────────────────────
function UpcomingRow({ ev, isLive, onTap }) {
  const d = daysUntil(ev.confirmedDateMs);
  const chip = d === 0 ? "Today" : d === 1 ? "Tomorrow" : fmtDateShort(ev.confirmedDateMs);
  const glow = eventGlow(ev);

  return (
    <button onClick={onTap} style={{
      display: "flex", alignItems: "center", gap: "0.75rem",
      cursor: "pointer", fontFamily: "inherit",
      background: `linear-gradient(90deg, ${glow.bg} 0%, transparent 40%)`,
      border: "1px solid rgba(255,218,173,0.1)",
      borderRadius: "12px", padding: "0.75rem 0.95rem",
      textDecoration: "none", color: "inherit",
      borderLeft: `3px solid ${glow.dot}`,
      textAlign: "left", width: "100%", boxSizing: "border-box",
      margin: 0, appearance: "none", WebkitAppearance: "none",
    }}>
      <div style={{ minWidth: "95px", flexShrink: 0 }}>
        <div style={{ color: "var(--jop-gold)", fontSize: "0.8rem", fontWeight: 800 }}>{chip}</div>
        {ev.confirmedStartMs && <div style={{ color: "rgba(255,218,173,0.4)", fontSize: "0.72rem" }}>{fmtTime(ev.confirmedStartMs)}</div>}
      </div>
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div style={{ color: "white", fontWeight: 700, fontSize: "0.9rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {ev.publicTitle || ev.eventName}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {(ev.publicLocation || ev.venueName) && (
            <span style={{ color: "rgba(255,218,173,0.45)", fontSize: "0.74rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {ev.publicLocation || ev.venueName}
            </span>
          )}
          {ev.isRecurring && ev.recurrenceHint && (
            <span style={{
              background: "rgba(74,164,225,0.12)", border: "1px solid rgba(74,164,225,0.3)",
              borderRadius: "999px", padding: "0.15rem 0.5rem",
              color: "var(--jop-highlight-blue)", fontSize: "0.65rem", fontWeight: 700,
              whiteSpace: "nowrap", flexShrink: 0,
            }}>
              {ev.recurrenceHint}
            </span>
          )}
        </div>
      </div>
      {isLive && <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#70be44", boxShadow: "0 0 6px rgba(112,190,68,0.8)", animation: "jopPulse 2s ease-in-out infinite", flexShrink: 0 }} />}
    </button>
  );
}

// ── Ascending fairy-dust particles ────────────────────────────────────────────
// Reuses the jopFairyDust keyframe from index.css (same effect as the OBS
// overlay top bar and PancakeDrift bottom glow). Small glowing dots that
// slowly ascend from the bottom of their container and fade out at the top.
// CellDust: 5 dots per calendar cell. PopoverDust: 10 dots per popover.

// Fairy dust — ascending glowing dots with box-shadow halos. The memory
// cost is negligible since only the current month's cells render (~20-30
// cells, not 142). 8 per cell, staggered heights so cells read as magical
// immediately.
function CellDust({ color, count = 8, seed = 1 }) {
  const dots = useMemo(() => {
    const hash = (s) => ((s * 9301 + 49297) % 233280) / 233280;
    return Array.from({ length: count }, (_, i) => {
      const h1 = hash(seed * 100 + i * 41);
      const h2 = hash(seed * 100 + i * 41 + 7);
      const h3 = hash(seed * 100 + i * 41 + 17);
      return {
        left: 8 + h1 * 84,
        bottom: i < 3 ? 2 : (25 + h3 * 30),
        bottomUnit: i < 3 ? "px" : "%",
        size: 1.5 + h2 * 2,
        delay: h3 * 5,
        dur: 2.5 + h1 * 3,
        peak: 0.35 + h2 * 0.5,
      };
    });
  }, [seed, count]);

  return dots.map((d, i) => (
    <span key={i} style={{
      position: "absolute",
      left: `${d.left}%`, bottom: `${d.bottom}${d.bottomUnit}`,
      width: `${d.size}px`, height: `${d.size}px`,
      borderRadius: "50%", background: color,
      boxShadow: `0 0 ${d.size + 2}px ${color}`,
      opacity: 0,
      animation: `jopFairyDust ${d.dur}s linear ${d.delay}s infinite`,
      "--jop-dust-peak": d.peak,
      pointerEvents: "none", zIndex: 1,
    }} />
  ));
}

function PopoverDust({ color }) {
  return <CellDust color={color} count={10} seed={99} />;
}

// ── Single-event modal (from Up Next tap) ────────────────────────────────────
// Centered overlay that shows one event card with full expand capability.
function EventModal({ ev, isLive, onClose }) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 400;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const pad = 12;
  const popW = Math.min(380, vw - pad * 2);
  const isMobile = vw < 600;
  const glowColor = eventGlow(ev).dot;

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(6,12,25,0.5)", zIndex: 200 }} />
      <div style={{
        position: "fixed",
        top: "15vh",
        left: "50%", transform: "translateX(-50%)",
        width: `${popW}px`,
        maxWidth: `calc(100vw - ${pad * 2}px)`,
        maxHeight: `${vh * 0.7}px`,
        overflowY: "auto", overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        background: "linear-gradient(180deg, #0e2644 0%, #0b1d34 100%)",
        border: "1px solid rgba(252,179,31,0.3)",
        borderRadius: "16px", padding: "1.1rem 1.1rem 1.25rem",
        zIndex: 201, fontFamily: "var(--jop-display)", color: "var(--jop-cream)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.55), 0 0 20px rgba(252,179,31,0.08)",
        animation: "jopPopCentered 0.18s ease-out",
      }}>
        <PopoverDust color={glowColor} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", position: "relative", zIndex: 1 }}>
          <h3 style={{ margin: 0, fontFamily: "var(--jop-script)", fontSize: "1.35rem", color: "var(--jop-gold)" }}>
            {fmtDateLong(ev.confirmedDateMs)}
          </h3>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <DayModalCard ev={ev} isLive={isLive} />
        </div>
      </div>
    </>
  );
}

// Calendar header fairy dust — particles rising from the MYC gradient edge.
// Uses the same jopFairyDust keyframe. Colors cycle through the pan-flag
// palette (magenta → gold → cyan) to match the gradient below them.
// 18 particles cycling through MYC pan-flag colors with box-shadow halos.
function CalendarHeaderDust() {
  const MYC = ["var(--jop-pink)", "var(--jop-gold)", "var(--jop-highlight-blue)", "var(--jop-cream)"];
  const dots = useMemo(() =>
    Array.from({ length: 18 }, (_, i) => ({
      left: 3 + (i * 5.5) % 94,
      bottom: i < 8 ? 0 : (15 + (i * 13) % 35),
      bottomUnit: i < 8 ? "px" : "%",
      size: 1.5 + (i % 3),
      color: MYC[i % MYC.length],
      delay: (i * 0.4) % 6,
      dur: 2.5 + (i % 4),
      peak: 0.35 + (i % 5) * 0.1,
    }))
  , []); // eslint-disable-line react-hooks/exhaustive-deps

  return dots.map((d, i) => (
    <span key={i} style={{
      position: "absolute",
      left: `${d.left}%`, bottom: `${d.bottom}${d.bottomUnit}`,
      width: `${d.size}px`, height: `${d.size}px`,
      borderRadius: "50%", background: d.color,
      boxShadow: `0 0 ${d.size + 3}px ${d.color}`,
      opacity: 0,
      animation: `jopFairyDust ${d.dur}s linear ${d.delay}s infinite`,
      "--jop-dust-peak": d.peak,
      pointerEvents: "none", zIndex: 0,
    }} />
  ));
}

// ── Shared tiny components ───────────────────────────────────────────────────
function LiveBadge() {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "0.4rem",
      background: "rgba(112,190,68,0.15)", border: "1px solid rgba(112,190,68,0.5)",
      borderRadius: "999px", padding: "0.3rem 0.75rem", marginBottom: "0.5rem",
    }}>
      <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#70be44", boxShadow: "0 0 6px rgba(112,190,68,0.8)", animation: "jopPulse 2s ease-in-out infinite" }} />
      <span style={{ color: "#70be44", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>Cooking now</span>
    </div>
  );
}
function StatusBanner({ msg }) {
  return <div style={{ background: "rgba(243,156,18,0.1)", border: "1px solid rgba(243,156,18,0.4)", borderRadius: "8px", padding: "0.45rem 0.7rem", marginBottom: "0.6rem", color: "#f5c842", fontSize: "0.82rem", fontWeight: 600 }}>{msg}</div>;
}
function Chip({ bg, border, color, children, style = {} }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", background: bg, border: `1px solid ${border}`, color, borderRadius: "999px", padding: "0.35rem 0.8rem", fontSize: "0.76rem", fontWeight: 700, ...style }}>{children}</span>;
}
function chipStyle(bg, border, color) {
  return { display: "inline-flex", alignItems: "center", gap: "0.3rem", background: bg, border: `1px solid ${border}`, color, borderRadius: "999px", padding: "0.4rem 0.8rem", fontSize: "0.76rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
}
const navBtn = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,218,173,0.15)", color: "rgba(255,218,173,0.7)", borderRadius: "10px", padding: "0.5rem 0.85rem", fontSize: "1.1rem", cursor: "pointer", fontFamily: "inherit" };
const closeBtn = { background: "transparent", border: "1px solid rgba(255,218,173,0.25)", color: "rgba(255,218,173,0.75)", width: "34px", height: "34px", borderRadius: "999px", fontSize: "1.1rem", cursor: "pointer", fontFamily: "inherit" };
